import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV, LOGO } from "../lib/site";
import { useSite, whatsappLink } from "../context/SiteContext";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { settings } = useSite();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "border-b border-[#111111]/10 bg-white/92 backdrop-blur-md"
          : "border-b border-transparent bg-white/0"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-8 px-6 py-4 lg:px-10">
        <Link to="/" data-testid="nav-logo" className="mako-focus flex items-center gap-3">
          <img src={LOGO.primary} alt="MAKO Mats" className="h-9 w-auto lg:h-11" />
        </Link>

        <nav className="hidden items-center gap-7 xl:flex">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.to.slice(1)}`}
              className={({ isActive }) =>
                `mako-underline-link mako-focus text-[0.6875rem] font-bold uppercase tracking-[0.16em] transition-colors duration-300 ${
                  isActive ? "text-[#0F4C81]" : "text-[#111111] hover:text-[#0F4C81]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={whatsappLink(settings)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp"
            className="mako-focus hidden items-center gap-2 border border-[#111111]/15 px-4 py-3 text-[0.625rem] font-bold uppercase tracking-[0.18em] text-[#111111] transition-colors duration-300 hover:border-[#0F4C81] hover:text-[#0F4C81] lg:flex"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            WhatsApp us
          </a>
          <Link
            to="/contact"
            data-testid="nav-quote-cta"
            className="mako-focus hidden bg-[#0F4C81] px-5 py-3.5 text-[0.625rem] font-bold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[#1463A5] sm:block"
          >
            Request a quote
          </Link>
          <button
            type="button"
            aria-label="Open menu"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="mako-focus flex h-11 w-11 items-center justify-center border border-[#111111]/15 text-[#111111] xl:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="border-t border-[#111111]/10 bg-white px-6 pb-10 pt-6 xl:hidden"
        >
          <div className="flex flex-col">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.to.slice(1)}`}
                className="mako-focus border-b border-[#111111]/8 py-4 font-display text-lg font-semibold text-[#111111]"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div className="mt-8 flex flex-col gap-3">
            <Link
              to="/contact"
              className="bg-[#0F4C81] px-6 py-4 text-center text-[0.6875rem] font-bold uppercase tracking-[0.2em] text-white"
            >
              Request a quote
            </Link>
            <a
              href={whatsappLink(settings)}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#111111]/15 px-6 py-4 text-center text-[0.6875rem] font-bold uppercase tracking-[0.2em] text-[#111111]"
            >
              WhatsApp MAKO
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
